import { useState, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { Table, Alert, Button, Spinner } from "reactstrap";
import * as ViewQL from "queries/view";
import { run } from "@shared/sql/engine";

// Running a view's query and showing what came back.
//
// The engine is in app/shared because the read-only viewer app a share link
// opens runs the same query; what differs between the two is only where the rows
// come from, which is the `loadTable` callback. Here it is a GraphQL read of the
// signed-in tenant's own table. There it will be the distribution's rows route.
export const QueryRunner = ({ query, params }) => {
  const client = useApolloClient();
  const [state, setState] = useState({ status: "idle" });

  // One table at a time, as the engine walks the AST — which is what makes a
  // view that reads one of five tables download one row set rather than five.
  const loadTable = useCallback(
    async (tableId) => {
      const { data } = await client.query({
        query: ViewQL.tableRows,
        variables: { tableId },
        // The rows are a concurrency-bearing read and a query is a point in
        // time; answering it from a cache filled by a different screen is how a
        // result set silently goes stale.
        fetchPolicy: "network-only",
      });
      const table = data?.viewer?.account?.tables?.get;
      if (!table) return null;
      return { columns: table.rows.columns, entries: table.rows.entries };
    },
    [client]
  );

  const execute = useCallback(async () => {
    setState({ status: "running" });
    try {
      const { rows, tables } = await run(query, { loadTable, params });
      setState({ status: "ok", rows, tables });
    } catch (e) {
      setState({ status: "error", error: String(e.message || e) });
    }
  }, [query, params, loadTable]);

  return (
    <>
      <Button color="primary" onClick={execute} disabled={state.status === "running" || !query}>
        {state.status === "running" ? <Spinner size="sm" /> : <i className="fa fa-play" />} Run
      </Button>

      <div className="mt-3" />

      {state.status === "error" && <QueryError error={state.error} />}
      {state.status === "ok" && <Results rows={state.rows} tables={state.tables} />}
    </>
  );
};

// The three errors a person can actually act on, said in words. Everything else
// is shown raw — a SQL syntax error from alasql already names the position, and
// paraphrasing it would lose that.
const QueryError = ({ error }) => {
  if (error.startsWith("table.missing:")) {
    const id = error.split("table.missing:")[1];
    return (
      <Alert color="danger">
        This query reads a table called <b>{id}</b>, and there is no such table.
      </Alert>
    );
  }
  if (error.startsWith("sql.ast.")) {
    return (
      <Alert color="danger">
        This query uses something we cannot check for safety — a table-valued
        function or a variable in <code>FROM</code>. Rewrite it against a table.
      </Alert>
    );
  }
  if (error === "sql.statements.expected-one") {
    return <Alert color="danger">A view is one query. Remove the second statement.</Alert>;
  }
  return <Alert color="danger"><pre className="mb-0">{error}</pre></Alert>;
};

const Results = ({ rows, tables }) => {
  if (!rows || rows.length === 0) {
    return <Alert color="light" className="border">No rows.</Alert>;
  }

  // The columns of the RESULT, not of any table: a query projects, renames and
  // computes, so the shape that comes back is the query's own.
  const keys = Object.keys(rows[0]);

  return (
    <>
      <div className="text-muted small mb-2">
        {rows.length.toLocaleString()} row{rows.length === 1 ? "" : "s"}
        {tables && tables.length ? <> from {tables.join(", ")}</> : null}
      </div>
      <div style={{ overflowX: "auto" }}>
        <Table size="sm" bordered>
          <thead>
            <tr>{keys.map((k) => <th key={k}>{k}</th>)}</tr>
          </thead>
          <tbody>
            {rows.slice(0, 500).map((row, i) => (
              <tr key={i}>
                {keys.map((k) => (
                  <td key={k}>
                    {/* A scalar function can return an object — urlImage and
                        airtableFirstImage both do — so a cell has to survive
                        one rather than render [object Object]. */}
                    {row[k] !== null && typeof row[k] === "object"
                      ? JSON.stringify(row[k])
                      : String(row[k] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {rows.length > 500 && (
        <div className="text-muted small">Showing the first 500.</div>
      )}
    </>
  );
};

export default QueryRunner;
